import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { useApp } from '../../context/AppContext';

const TABS = [
  { label: 'Property Managers', to: '/property-managers' },
  { label: 'Contractors', to: '/contractors' },
  { label: 'Franchise Operators', to: '/franchise' },
  { label: 'Contact', to: '/contact' },
];

const ROLES = [
  { id: 'manager', label: 'Landlord / Agent', icon: 'Building2' },
  { id: 'contractor', label: 'Contractor', icon: 'Wrench' },
  { id: 'admin', label: 'Admin', icon: 'ShieldCheck' },
];

const SiteNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, quickLogin, logout } = useApp();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const currentRole = ROLES.find((r) => r.id === user?.role);
  const switchRole = async (id) => { try { await quickLogin(id); navigate('/dashboard'); } catch (e) { navigate('/login'); } setMobile(false); };
  const isActive = (to) => location.pathname === to;

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all ${scrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur border-b border-border'}`}>
      <div className="max-w-7xl mx-auto px-6 h-[68px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="h-9 w-9 rounded-lg bg-[#102d55] text-white flex items-center justify-center"><Icons.House className="h-5 w-5" /></span>
          <span className="font-display font-extrabold text-lg tracking-tight text-[#102d55]">Check-a-Property</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {TABS.map((t) => (
            <button key={t.to} onClick={() => navigate(t.to)} className={`px-3.5 py-2 text-sm font-medium rounded-lg transition-colors ${isActive(t.to) ? 'text-[#102d55] bg-secondary' : 'text-slate-600 hover:text-[#102d55] hover:bg-secondary'}`}>{t.label}</button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="hidden sm:flex gap-2 border-slate-300">
                  <Icons.UserCircle2 className="h-4 w-4 text-[#102d55]" />
                  <span className="max-w-[120px] truncate">{currentRole?.label || user.name}</span>
                  <Icons.ChevronDown className="h-4 w-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel>Signed in as<br /><span className="font-normal text-muted-foreground">{user.name} · {user.org}</span></DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard')} className="gap-2 cursor-pointer"><Icons.LayoutDashboard className="h-4 w-4" /> Dashboard</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground">Switch demo role</DropdownMenuLabel>
                {ROLES.map((r) => { const C = Icons[r.icon] || Icons.User; return (
                  <DropdownMenuItem key={r.id} onClick={() => switchRole(r.id)} className="gap-2 cursor-pointer">
                    <C className="h-4 w-4" /> {r.label}{r.id === user.role && <Icons.Check className="h-4 w-4 ml-auto text-[#1c63c4]" />}
                  </DropdownMenuItem>
                ); })}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} className="gap-2 cursor-pointer text-destructive"><Icons.LogOut className="h-4 w-4" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="outline" onClick={() => navigate('/login')} className="hidden sm:flex gap-2 border-slate-300"><Icons.LogIn className="h-4 w-4" /> Sign in</Button>
          )}
          <Button onClick={() => navigate('/book')} className="bg-[#102d55] hover:bg-[#0b2244] text-white font-semibold">Book Now</Button>
          <button className="lg:hidden ml-1" onClick={() => setMobile((m) => !m)}>{mobile ? <Icons.X className="h-6 w-6" /> : <Icons.Menu className="h-6 w-6" />}</button>
        </div>
      </div>

      {mobile && (
        <div className="lg:hidden bg-white border-t border-border px-6 py-3 space-y-1">
          {TABS.map((t) => <button key={t.to} onClick={() => { navigate(t.to); setMobile(false); }} className="block w-full text-left py-2 text-sm font-medium text-slate-700">{t.label}</button>)}
          <div className="pt-2 border-t border-border mt-2">
            {user ? (<>
              <div className="text-xs text-muted-foreground py-1">Switch role</div>
              {ROLES.map((r) => <button key={r.id} onClick={() => switchRole(r.id)} className="block w-full text-left py-2 text-sm font-medium text-[#102d55]">{r.label}</button>)}
              <button onClick={() => { logout(); navigate('/'); }} className="block w-full text-left py-2 text-sm font-medium text-destructive">Sign out</button>
            </>) : (
              <button onClick={() => { navigate('/login'); setMobile(false); }} className="block w-full text-left py-2 text-sm font-medium text-[#102d55]">Sign in</button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default SiteNav;
